import { Helmet } from "react-helmet-async";
import { GrapeshotDataObject } from "@app/types/Cue";

import {
  ANDRIOD_APP_NAME,
  ANDRIOD_PACKAGE,
  FB_ADMINS,
  FB_APP_ID,
  FB_PAGES,
  FORMAT_DETECTION,
  GOOGLE_SITE_VERIFICATION,
  IOS_APP_NAME,
  IOS_APP_STORE_ID,
  OG_SITE_NAME,
  TWITTER_APP_COUNTRY,
  TWITTER_APP_ID_GOOGLEPLAY,
  TWITTER_APP_ID_IPAD,
  TWITTER_APP_ID_IPHONE,
  TWITTER_CARD,
  TWITTER_SITE,
  URL_ORIGIN,
  URL_ORIGIN_ALTERNATE,
  URL_ORIGIN_TWITTER,
} from "./constants";

export type MetaTagsProps = {
  title: string;
  author?: string;
  description: string;
  keywords?: string[];
  slug: string;
  shortLink?: string;
  canonicalUrl?: string;
  ogType?: string;
  articlePageclass?: string;
  imageUrl?: string;
  imageAlt?: string;
  robots?: string;
  jsonLd?: object;
  ogTitle?: string;
  gsChannels?: GrapeshotDataObject[];
};

export default function MetaTags({
  title,
  author,
  description,
  keywords = [],
  slug = "",
  shortLink,
  canonicalUrl,
  ogType = "article",
  articlePageclass = "article",
  imageUrl,
  imageAlt,
  robots,
  jsonLd,
  ogTitle,
  gsChannels = [],
}: MetaTagsProps) {
  const alternateUrl: string = URL_ORIGIN_ALTERNATE + slug;
  const twitterAppUrl: string = URL_ORIGIN_TWITTER + slug;
  const mobileUrl: string = URL_ORIGIN + slug;
  canonicalUrl = canonicalUrl ? canonicalUrl : URL_ORIGIN + slug;
  shortLink = shortLink ? URL_ORIGIN + shortLink : "";
  const gsChannelsNames = gsChannels.map((item) => item.name);

  return (
    <Helmet>
      <title>{title}</title>

      {/* Search Engine tags */}
      {robots ? <meta name="robots" content={robots} /> : null}
      <link rel="alternate" href={alternateUrl} />
      <link rel="canonical" href={canonicalUrl} />
      {shortLink ? <link rel="shortlink" href={shortLink} /> : null}
      {description ? <meta name="description" content={description} /> : null}
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {author ? <meta name="author" content={author} /> : null}
      <meta name="article:pageclass" content={articlePageclass} />
      <meta
        name="google-site-verification"
        content={GOOGLE_SITE_VERIFICATION}
      />
      <meta name="format-detection" content={FORMAT_DETECTION} />

      {/* Open Graph tags */}
      <meta property="og:site_name" content={OG_SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={ogTitle ? ogTitle : title} />
      {description ? (
        <meta property="og:description" content={description} />
      ) : null}
      {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
      {imageAlt ? <meta property="og:image:alt" content={imageAlt} /> : null}

      {/* Facebook tags */}
      <meta property="fb:admins" content={FB_ADMINS} />
      <meta property="fb:pages" content={FB_PAGES} />
      <meta property="fb:app_id" content={FB_APP_ID} />

      {/* Twitter tags */}
      <meta property="twitter:card" content={TWITTER_CARD} />
      <meta property="twitter:site" content={TWITTER_SITE} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:url" content={canonicalUrl} />
      {description ? (
        <meta property="twitter:description" content={description} />
      ) : null}
      {imageUrl ? <meta property="twitter:image" content={imageUrl} /> : null}
      {imageAlt ? (
        <meta property="twitter:image:alt" content={imageAlt} />
      ) : null}
      <meta property="twitter:app:country" content={TWITTER_APP_COUNTRY} />
      <meta property="twitter:app:id:iphone" content={TWITTER_APP_ID_IPHONE} />
      <meta property="twitter:app:url:iphone" content={twitterAppUrl} />
      <meta property="twitter:app:id:ipad" content={TWITTER_APP_ID_IPAD} />
      <meta property="twitter:app:url:ipad" content={twitterAppUrl} />
      <meta
        property="twitter:app:id:googleplay"
        content={TWITTER_APP_ID_GOOGLEPLAY}
      />
      <meta property="twitter:app:url:googleplay" content={twitterAppUrl} />

      {/* Mobile tags */}
      <meta property="al:ios:url" content={mobileUrl} />
      <meta property="al:ios:app_store_id" content={IOS_APP_STORE_ID} />
      <meta property="al:ios:app_name" content={IOS_APP_NAME} />
      <meta property="al:android:url" content={mobileUrl} />
      <meta property="al:android:package" content={ANDRIOD_PACKAGE} />
      <meta property="al:android:app_name" content={ANDRIOD_APP_NAME} />
      <meta property="al:web:url" content={mobileUrl} />

      {/* Grapeshot tags */}
      <meta
        name="grapeshot_tag"
        id="grapeshot"
        data-testid="grapeshot"
        content={gsChannelsNames.join(",")}
      />
      <script>{`var gs_channels = ${JSON.stringify(gsChannelsNames)};`}</script>

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
